<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	sections: $.array(
		'Sections',
		defineSchema({
			label: $.text('Nav Label'),
			title: $.text('Section Title'),
			content: $.richtext('Content'),
			link: $.text('External Link (optional)'),
		}),
		{
			collapsible: true,
			allowAdd: true,
			allowDelete: true,
			allowReorder: true,
			default: [
				{ label: 'About', title: 'About', content: '', link: '' },
				{ label: 'Staff', title: 'Staff', content: '', link: '' },
				{ label: 'Supporters', title: 'Supporters', content: '', link: '' },
				{ label: 'Our Values', title: 'Our Values', content: '', link: '' },
				{ label: 'Testimonials', title: 'Testimonials', content: '', link: '' },
				{ label: 'Authors', title: '', content: '', link: '/authors' },
				{ label: 'Submissions', title: 'Submissions', content: '', link: '' },
			],
		},
	),
	maxWidth: $.select('Max width', [...CONTENT_WIDTH_OPTIONS], { default: '1100' }),
	...pageHeightField(true),
})
export const BlockAbout = defineBlock({
	thumbnail: '/block-thumbs/BlockAbout.svg',
	category: 'Content',
	order: 15,
	name: 'BlockAbout',
	label: 'About (with side nav)',
	icon: 'menu_book',
	schema,
	// The block reads this collection directly. Declaring it lets the production
	// build grant expose it during prerender route discovery.
	dataSource: { collection: 'about-pages', store: 'the_torah', label: 'About Pages' },
})
</script>
<script setup lang="ts">
import { useSeo } from '@bagelink/blox'
import { Btn } from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCollection } from '@/composables/useDatastore'
import { sanitizeArticleHtml } from '@/utils/articleHtml'
import { buildAboutSections, type AboutSectionItem } from '@/utils/aboutSections'
import { pageSeo } from '@/utils/seo'
import {
	CONTENT_WIDTH_OPTIONS,
	pageHeightClass,
	pageHeightField,
	widthClass,
} from '@/utils/blockLayout'
import RichHtml from '@/components/RichHtml.vue'



interface AboutRecord {
	name?: string
	slug?: string
	content?: string
	order?: number
	titleTag?: string
	metaDescription?: string
}

/** Nav order on the live About section. Records not listed follow their stored order. */
const ABOUT_ORDER = ['about-us', 'staff', 'supporters', 'our-values', 'testimonials', 'submissions']

const { items: aboutPages } = useCollection<AboutRecord>('about-pages', {
	all: true,
	pick: ['_id', 'name', 'slug', 'content', 'order', 'titleTag', 'metaDescription'],
})

const props = defineProps<{
	sections?: AboutSectionItem[]
	maxWidth?: string
	pageHeight?: boolean
}>()

const rootClass = computed(() =>
	[widthClass(props.maxWidth, '1100'), pageHeightClass(props.pageHeight ?? true)]
		.filter(Boolean)
		.join(' '),
)

const sections = computed<AboutSectionItem[]>(() => {
	const stored = aboutPages.value
		.filter(page => page.slug && page.name)
		.sort((a, b) => {
			const left = ABOUT_ORDER.indexOf(a.slug || '')
			const right = ABOUT_ORDER.indexOf(b.slug || '')
			if (left >= 0 || right >= 0) return (left < 0 ? 99 : left) - (right < 0 ? 99 : right)
			return (a.order ?? 999) - (b.order ?? 999)
		})
		.map(page => ({
			label: page.name || '',
			title: page.name || '',
			content: sanitizeArticleHtml(page.content),
			slug: page.slug,
		}))
	const authored = (props.sections ?? []).filter(section => section.label)
	return buildAboutSections(stored, authored)
})

// Only sections that render inline content (no external link) can be "active".
const contentSections = computed(() => sections.value.filter(section => !section.link))

const activeIndex = ref(0)
const activeSection = computed(() => contentSections.value[activeIndex.value] ?? null)
const route = useRoute()

function sectionId(section: AboutSectionItem) {
	return section.slug || (section.label || section.title || '')
		.toLowerCase()
		.replace(/[’']/g, '')
		.replace(/\s+/g, '-')
}

/** Each section is its own page. `about-us` is the index at `/about`. */
function sectionPath(section: AboutSectionItem) {
	const slug = sectionId(section)
	return slug === 'about-us' ? '/about' : `/about/${slug}`
}

function requestedSection(path: string, hash: string) {
	const query = new URLSearchParams(path.split('?')[1] || '').get('section')
	if (query) return query
	if (path.startsWith('/about/')) return path.slice('/about/'.length).split(/[?#]/)[0]
	return hash.slice(1)
}

watch(
	() => [route.fullPath, route.hash, contentSections.value] as const,
	([path, hash]) => {
		const requested = requestedSection(path, hash)
		if (!requested || requested === 'about-us') {
			activeIndex.value = 0
			return
		}
		const index = contentSections.value.findIndex(section => sectionId(section) === requested)
		if (index >= 0) activeIndex.value = index
	},
	{ immediate: true },
)

useSeo(() => {
	const section = activeSection.value
	if (!section?.slug || section.slug === 'about-us') return null
	const record = aboutPages.value.find(page => page.slug === section.slug)
	return record ? pageSeo(sectionPath(section), record) : null
})

function isActive(section: AboutSectionItem) {
	return !section.link && contentSections.value.indexOf(section) === activeIndex.value
}

function selectSection(section: AboutSectionItem) {
	if (section.link) return
	activeIndex.value = contentSections.value.indexOf(section)
}
</script>

<template>
	<div class="pb-3 pt-4 m_pt-2 m_w-100p m_px-05" :class="rootClass">
		<h1
			v-if="activeSection?.title"
			class="m-0 txt40 regular line-height-14 m_txt32 pb-2-5 txt-center m_txt-start m_pb-1 m_px-1"
		>
			{{ activeSection.title }}
		</h1>

		<div class="flex align-items-start gap-1 m_block m_overflow-hidden1">
			<nav
				class="sticky-3 h-auto bg-gray flex column py-1-5 px-2 m_bg-white m_row m_filters-mask m_overflow m_px-0 m_pb-0 m_border-bottom m_mb-1 m_top-3 m_pt-2"
			>
				<Btn
					v-for="section in sections"
					:key="section.slug || section.label"
					class="txt17 py-1-5 w-100p m_w-auto m_flex-shrink-0 m_px-1"
					align-txt="start"
					flat
					:class="{ 'color-red': isActive(section) }"
					:to="section.link || sectionPath(section)"
					:aria-current="isActive(section) ? 'page' : undefined"
					@click="selectSection(section)"
				>
					{{ section.label }}
				</Btn>
			</nav>

			<RichHtml
				v-if="activeSection"
				:id="sectionId(activeSection)"
				class="about-section px-1 flex-grow"
				tag="section"
				:html="activeSection.content"
			/>
		</div>
	</div>
</template>

<style></style>
